import {
	CARGO_TYPE_ID,
	SHIPMENT_EDIT_WINDOW,
	SHIPMENT_STATUS,
	TAccessState,
	TEditShipmentAccess,
	TEditShipmentUserType,
	TShipmentOrder,
} from '@types';
import { isShipmentInvoiced } from './isShipmentInvoiced';
import { isSuperdockShipperLeg } from './isSuperdockShipperLeg';

type TShipperBucket = 'pre-allocation' | 'post-allocation' | 'post-complete-or-invoiced';
type TAdminBucket = 'pre-allocation' | 'post-allocation' | 'post-delivery' | 'post-shipper-invoice';

type TMatrixCell = { visible: boolean; editable: boolean };

type TMatrixRow = {
	updateETA: TMatrixCell;
	cargo: TMatrixCell;
	modifiers: TMatrixCell;
	location: TMatrixCell;
	contactDetails: TMatrixCell;
	driverInfo: TMatrixCell;
	referenceNumbers: TMatrixCell;
	additionalDetails: TMatrixCell;
};

export const ADMIN_ETA_REDIRECT_REASON = 'Please perform this action via the Transport page';

const REASON = {
	READ_ONLY: 'This section is currently view-only.',
	LOCATION_UNSUPPORTED: 'Editing address details is not yet supported.',
	SUPERDOCK: 'Cargo for this shipment is managed in Superdock.',
	SUPERDOCK_MODIFIERS: 'This shipment has been superdocked. Modifiers cannot be changed.',
	SAVE_BLOCKED: 'This shipment is read-only and cannot be saved.',
	WARN_CARGO_INVOICED: 'Update Cargo is disabled because an invoice has already been generated for this shipment.',
	WARN_MULTI_CARGO: 'Cargo cannot be edited on a shipment with several freight lines.',
	WARN_CARGO_EDIT_CLOSED: 'Update Cargo is disabled because this shipment can no longer be adjusted.',
	WARN_MODIFIERS_EDIT_CLOSED: 'Update Modifiers is disabled because this shipment can no longer be adjusted.',
	WARN_ADDITIONAL_INVOICED: 'Update Additional Details is disabled because an invoice has already been generated for this shipment.',
	ADMIN_ETA_REDIRECT: ADMIN_ETA_REDIRECT_REASON,
};

const EDITABLE_CELL: TMatrixCell = { visible: true, editable: true };
const READONLY_CELL: TMatrixCell = { visible: true, editable: false };
const HIDDEN_CELL: TMatrixCell = { visible: false, editable: false };

const SHIPPER_MATRIX: Record<TShipperBucket, TMatrixRow> = {
	'pre-allocation': {
		updateETA: EDITABLE_CELL,
		cargo: HIDDEN_CELL,
		modifiers: HIDDEN_CELL,
		location: READONLY_CELL,
		contactDetails: EDITABLE_CELL,
		driverInfo: EDITABLE_CELL,
		referenceNumbers: EDITABLE_CELL,
		additionalDetails: EDITABLE_CELL,
	},
	'post-allocation': {
		updateETA: EDITABLE_CELL,
		cargo: HIDDEN_CELL,
		modifiers: HIDDEN_CELL,
		location: READONLY_CELL,
		contactDetails: EDITABLE_CELL,
		driverInfo: EDITABLE_CELL,
		referenceNumbers: EDITABLE_CELL,
		additionalDetails: EDITABLE_CELL,
	},
	'post-complete-or-invoiced': {
		updateETA: READONLY_CELL,
		cargo: READONLY_CELL,
		/* Modifiers are admin-only, so they stay hidden for shippers in every bucket. */
		modifiers: HIDDEN_CELL,
		location: READONLY_CELL,
		contactDetails: READONLY_CELL,
		driverInfo: READONLY_CELL,
		referenceNumbers: READONLY_CELL,
		additionalDetails: READONLY_CELL,
	},
};

const ADMIN_MATRIX: Record<TAdminBucket, TMatrixRow> = {
	'pre-allocation': {
		updateETA: EDITABLE_CELL,
		cargo: EDITABLE_CELL,
		modifiers: EDITABLE_CELL,
		location: READONLY_CELL,
		contactDetails: EDITABLE_CELL,
		driverInfo: EDITABLE_CELL,
		referenceNumbers: EDITABLE_CELL,
		additionalDetails: EDITABLE_CELL,
	},
	'post-allocation': {
		updateETA: EDITABLE_CELL,
		cargo: EDITABLE_CELL,
		modifiers: EDITABLE_CELL,
		location: READONLY_CELL,
		contactDetails: EDITABLE_CELL,
		driverInfo: EDITABLE_CELL,
		referenceNumbers: EDITABLE_CELL,
		additionalDetails: EDITABLE_CELL,
	},
	'post-delivery': {
		updateETA: EDITABLE_CELL,
		cargo: EDITABLE_CELL,
		modifiers: EDITABLE_CELL,
		location: READONLY_CELL,
		contactDetails: READONLY_CELL,
		driverInfo: READONLY_CELL,
		referenceNumbers: EDITABLE_CELL,
		additionalDetails: READONLY_CELL,
	},
	'post-shipper-invoice': {
		updateETA: EDITABLE_CELL,
		cargo: EDITABLE_CELL,
		modifiers: EDITABLE_CELL,
		location: READONLY_CELL,
		contactDetails: READONLY_CELL,
		driverInfo: READONLY_CELL,
		referenceNumbers: EDITABLE_CELL,
		additionalDetails: READONLY_CELL,
	},
};

const isShipperSideInvoiced = (shipment: TShipmentOrder | null | undefined): boolean =>
	(shipment?.payment_status?.is_invoiced ?? false) || !!shipment?.shipper_payment_status?.code;

/* The backend closes cargo and modifier edits once the carrier is invoiced, and also for
   multi-cargo shipments; it hands us the outcome as the shipment's edit window rather than
   leaving us to rebuild the rule. An absent window means a response from before it was
   returned, which reads as no restriction. */
const isEditWindowClosed = (shipment: TShipmentOrder | null | undefined): boolean =>
	shipment?.edit_window === SHIPMENT_EDIT_WINDOW.NO_ADJUSTMENT;

const getShipperBucket = (shipment: TShipmentOrder | null | undefined): TShipperBucket => {
	const statusCode = shipment?.status?.code ?? 0;
	if (statusCode >= SHIPMENT_STATUS.DELIVERED || isShipmentInvoiced(shipment)) return 'post-complete-or-invoiced';
	if (shipment?.status?.short === 'Pending') return 'pre-allocation';
	return 'post-allocation';
};

const getAdminBucket = (shipment: TShipmentOrder | null | undefined): TAdminBucket => {
	if (isShipperSideInvoiced(shipment)) return 'post-shipper-invoice';
	const statusCode = shipment?.status?.code ?? 0;
	if (statusCode >= SHIPMENT_STATUS.DELIVERED) return 'post-delivery';
	if (shipment?.status?.short === 'Pending') return 'pre-allocation';
	return 'post-allocation';
};

const emptyAccess = (userType: TEditShipmentUserType): TEditShipmentAccess => {
	const hiddenField: TAccessState = { visible: false, disabled: false };
	return {
		userType,
		saveBlockedByPermission: true,
		saveReason: REASON.SAVE_BLOCKED,
		defaultPageId: userType === 'admin' ? 1 : 0,
		pages: {
			updateETA: { ...hiddenField, fields: { requestBooking: hiddenField } },
			cargo: {
				...hiddenField,
				fields: {
					cargoForm: hiddenField,
					priceAcceptance: hiddenField,
					updateButton: hiddenField,
					invoicedWarning: hiddenField,
					superdockRedirect: hiddenField,
					multiCargoWarning: hiddenField,
				},
			},
			modifiers: {
				...hiddenField,
				fields: {
					modifierForm: hiddenField,
					updateButton: hiddenField,
					invoicedWarning: hiddenField,
					superdockWarning: hiddenField,
				},
			},
			location: {
				...hiddenField, fields: {
					address: hiddenField, pickupTimes: hiddenField, dropoffTimes: hiddenField
				}
			},
			contactDetails: { ...hiddenField, fields: { inputs: hiddenField } },
			driverInfo: { ...hiddenField, fields: { inputs: hiddenField, documents: hiddenField } },
			referenceNumbers: { ...hiddenField, fields: { shipperRef: hiddenField } },
			additionalDetails: { ...hiddenField, fields: { loadSecurity: hiddenField, invoicedWarning: hiddenField } },
		},
	};
};

type TInputs = {
	userType: TEditShipmentUserType;
	shipment: TShipmentOrder | null | undefined;
	isAdminOnShipperPage?: boolean;
};

export const getEditShipmentAccess = ({ userType, shipment, isAdminOnShipperPage }: TInputs): TEditShipmentAccess => {
	if (!shipment) return emptyAccess(userType);

	const matrixRow: TMatrixRow = userType === 'admin'
		? ADMIN_MATRIX[getAdminBucket(shipment)]
		: SHIPPER_MATRIX[getShipperBucket(shipment)];

	/* Both sides of a Superdock are restricted: the admin leg (the Superdock's own
	   shipment) and the shipment leg (the shipper shipment pulled into it). */
	const isSuperdockShipment = !!shipment.is_admin_shipment || isSuperdockShipperLeg(shipment);
	const superdockActive = userType === 'admin' && isSuperdockShipment;
	const editWindowClosed = userType === 'admin' && isEditWindowClosed(shipment);

	const reasonForCell = (matrixCell: TMatrixCell, pageKey: keyof TMatrixRow): string | undefined => {
		if (!(matrixCell.visible && !matrixCell.editable)) return undefined;
		if (pageKey === 'location') return REASON.LOCATION_UNSUPPORTED;
		return REASON.READ_ONLY;
	};

	const toPageAccess = (matrixCell: TMatrixCell, pageKey: keyof TMatrixRow): TAccessState => ({
		visible: matrixCell.visible,
		disabled: matrixCell.visible && !matrixCell.editable,
		reason: reasonForCell(matrixCell, pageKey),
	});

	const base = toPageAccess(matrixRow.updateETA, 'updateETA');
	const updateETA = isAdminOnShipperPage
		? {
			...base, disabled: true, reason: REASON.ADMIN_ETA_REDIRECT
		}
		: base;
	const cargo = toPageAccess(matrixRow.cargo, 'cargo');
	const modifiers = toPageAccess(matrixRow.modifiers, 'modifiers');
	const location = toPageAccess(matrixRow.location, 'location');
	const contactDetails = toPageAccess(matrixRow.contactDetails, 'contactDetails');
	const driverInfo = toPageAccess(matrixRow.driverInfo, 'driverInfo');
	const referenceNumbers = toPageAccess(matrixRow.referenceNumbers, 'referenceNumbers');
	const additionalDetails = toPageAccess(matrixRow.additionalDetails, 'additionalDetails');

	/* Field-level only — the modifiers page cell feeds saveBlockedByPermission. */
	const modifiersSuperdockActive = modifiers.visible && superdockActive;
	const modifiersEditWindowClosed = modifiers.visible && editWindowClosed && !modifiersSuperdockActive;
	const modifierReason = modifiersSuperdockActive
		? REASON.SUPERDOCK_MODIFIERS
		: REASON.WARN_MODIFIERS_EDIT_CLOSED;
	const modifierFieldAccess: TAccessState = modifiersSuperdockActive || modifiersEditWindowClosed
		? {
			...modifiers, disabled: true, reason: modifierReason
		}
		: modifiers;

	/* Superdock keeps its redirect even with the window closed: it is the only thing telling the
	   admin where this shipment's cargo actually lives. */
	const cargoEditWindowClosed = cargo.visible && editWindowClosed && !superdockActive;
	const cargoMultiCargoActive = cargo.visible && !superdockActive
		&& shipment.cargo_type_id === CARGO_TYPE_ID.Other
		&& (shipment.cargos?.length ?? 0) > 1;
	const cargoShipperWarningVisible = cargo.visible && cargo.disabled
		&& !superdockActive && !cargoEditWindowClosed && !cargoMultiCargoActive;
	const cargoInvoicedWarningVisible = !cargoMultiCargoActive
		&& (cargoShipperWarningVisible || cargoEditWindowClosed);
	/* The shipper path hides the form behind its warning, so a visible-but-disabled form only
	   ever means a closed edit window. */
	const cargoFormVisible = cargo.visible && !superdockActive && !cargoShipperWarningVisible
		&& !cargoMultiCargoActive;
	const cargoFormDisabled = cargoFormVisible && cargoEditWindowClosed;
	const cargoFormAccess: TAccessState = {
		visible: cargoFormVisible,
		disabled: cargoFormDisabled,
		reason: cargoFormDisabled ? REASON.WARN_CARGO_EDIT_CLOSED : undefined,
	};

	const additionalInvoicedWarningVisible = additionalDetails.visible && additionalDetails.disabled
		&& (userType === 'shipper' || (userType === 'admin' && isShipperSideInvoiced(shipment)));

	const pages: TEditShipmentAccess['pages'] = {
		updateETA: {
			...updateETA,
			fields: { requestBooking: updateETA },
		},
		cargo: {
			...cargo,
			fields: {
				cargoForm: cargoFormAccess,
				priceAcceptance: cargoFormAccess,
				updateButton: cargoFormAccess,
				invoicedWarning: {
					visible: cargoInvoicedWarningVisible,
					disabled: false,
					reason: cargoEditWindowClosed ? REASON.WARN_CARGO_EDIT_CLOSED
						: (cargoShipperWarningVisible ? REASON.WARN_CARGO_INVOICED : undefined),
				},
				superdockRedirect: {
					visible: superdockActive,
					disabled: false,
					reason: superdockActive ? REASON.SUPERDOCK : undefined,
				},
				multiCargoWarning: {
					visible: cargoMultiCargoActive,
					disabled: false,
					reason: cargoMultiCargoActive ? REASON.WARN_MULTI_CARGO : undefined,
				},
			},
		},
		modifiers: {
			...modifiers,
			fields: {
				modifierForm: modifierFieldAccess,
				updateButton: modifierFieldAccess,
				invoicedWarning: {
					visible: modifiersEditWindowClosed,
					disabled: false,
					reason: modifiersEditWindowClosed ? REASON.WARN_MODIFIERS_EDIT_CLOSED : undefined,
				},
				superdockWarning: {
					visible: modifiersSuperdockActive,
					disabled: false,
					reason: modifiersSuperdockActive ? REASON.SUPERDOCK_MODIFIERS : undefined,
				},
			},
		},
		location: {
			...location,
			fields: {
				address: location,
				pickupTimes: location,
				dropoffTimes: location,
			},
		},
		contactDetails: {
			...contactDetails,
			fields: { inputs: contactDetails },
		},
		driverInfo: {
			...driverInfo,
			fields: { inputs: driverInfo, documents: driverInfo },
		},
		referenceNumbers: {
			...referenceNumbers,
			fields: { shipperRef: referenceNumbers },
		},
		additionalDetails: {
			...additionalDetails,
			fields: {
				loadSecurity: additionalDetails,
				invoicedWarning: {
					visible: additionalInvoicedWarningVisible,
					disabled: false,
					reason: additionalInvoicedWarningVisible ? REASON.WARN_ADDITIONAL_INVOICED : undefined,
				},
			},
		},
	};

	const visiblePageAccessStates: TAccessState[] = [updateETA, cargo, modifiers, location, contactDetails, driverInfo, referenceNumbers, additionalDetails,];
	const everyVisiblePageDisabled = visiblePageAccessStates.every((pageAccess) => !pageAccess.visible || pageAccess.disabled);

	return {
		userType,
		saveBlockedByPermission: everyVisiblePageDisabled,
		saveReason: everyVisiblePageDisabled ? REASON.SAVE_BLOCKED : undefined,
		defaultPageId: userType === 'admin' ? 1 : 0,
		pages,
	};
};
